import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHero from '../components/PageHero';
import OptimizedImage from '../components/OptimizedImage';
import MealComposerModal from '../components/MealComposerModal';
import SEO from '../components/SEO';
import { PAGE_SEO } from '../config/seo';
import { menuItems, menuCategories } from '../data/menuData';
import { formatPrice } from '../utils/formatPrice';
import { searchMenuItems } from '../utils/searchMenu';
import { useCart } from '../context/CartContext';
import { createCartItemFromProduct, isComposableProduct } from '../utils/cartItem';

function Menu() {
  const { addItem } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const searchQuery = (searchParams.get('q') || '').trim();
  const openProductId = searchParams.get('open');
  const initialCategory = menuCategories.some((cat) => cat.id === categoryParam)
    ? categoryParam
    : 'all';
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    if (categoryParam && menuCategories.some((cat) => cat.id === categoryParam)) {
      setActiveCategory(categoryParam);
    }
  }, [categoryParam]);

  useEffect(() => {
    if (!openProductId) return;

    const product = menuItems.find((item) => item.id === openProductId);
    if (product) {
      if (isComposableProduct(product)) {
        setSelectedProduct(product);
      } else {
        addItem(createCartItemFromProduct(product));
      }
    }

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('open');
      return next;
    }, { replace: true });
  }, [openProductId, setSearchParams, addItem]);

  const filteredItems = useMemo(() => {
    const byCategory = activeCategory === 'all'
      ? menuItems
      : menuItems.filter((item) => item.category === activeCategory);

    if (!searchQuery) return byCategory;

    const searched = searchMenuItems(searchQuery, { limit: 100 });
    if (activeCategory === 'all') return searched;

    return searched.filter((item) => item.category === activeCategory);
  }, [activeCategory, searchQuery]);

  const openComposer = (product) => setSelectedProduct(product);

  const handleProductAction = (product) => {
    if (isComposableProduct(product)) {
      openComposer(product);
      return;
    }
    addItem(createCartItemFromProduct(product));
  };

  const closeComposer = () => setSelectedProduct(null);

  const clearSearch = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('q');
    setSearchParams(nextParams);
  };

  return (
    <div className="inner-page menu-page">
      <SEO {...PAGE_SEO.menu} />
      <PageHero
        tag="Nos spécialités"
        title="Notre menu"
        subtitle="Burgers · Crêpes · Tacos et plus — préparés avec passion"
      />

      <section className="page-content page-content-cream">
        {searchQuery && (
          <div className="menu-search-banner">
            <p>
              {filteredItems.length > 0
                ? `${filteredItems.length} résultat${filteredItems.length > 1 ? 's' : ''} pour « ${searchQuery} »`
                : `Aucun résultat pour « ${searchQuery} »`}
            </p>
            <button type="button" className="menu-search-clear" onClick={clearSearch}>
              Effacer la recherche
            </button>
          </div>
        )}

        <div className="category-pills">
          {menuCategories.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={`pill ${activeCategory === id ? 'active' : ''}`}
              onClick={() => setActiveCategory(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {filteredItems.length > 0 ? (
          <div className="menu-items-grid">
            {filteredItems.map((item) => {
              const composable = isComposableProduct(item);
              return (
              <article
                key={item.id}
                className={`menu-item-card ${composable ? 'menu-item-card-clickable' : 'menu-item-card-simple'}`}
                onClick={composable ? () => handleProductAction(item) : undefined}
                onKeyDown={composable ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleProductAction(item);
                  }
                } : undefined}
                role={composable ? 'button' : undefined}
                tabIndex={composable ? 0 : undefined}
                aria-label={composable ? `Composer ${item.name}` : undefined}
              >
                <div className="menu-item-image">
                  <OptimizedImage image={item.image} alt={item.name} loading="lazy" />
                </div>
                <div className="menu-item-body">
                  <h3 className="menu-item-name">{item.name}</h3>
                  <p className="menu-item-desc">{item.desc}</p>
                  {item.category === 'packs' && item.contents?.length > 0 && (
                    <ul className="menu-item-contents">
                      {item.contents.map((entry) => (
                        <li key={entry}>{entry}</li>
                      ))}
                    </ul>
                  )}
                  <div className="menu-item-footer">
                    <span className="menu-item-price">{formatPrice(item.basePrice)}</span>
                    <button
                      type="button"
                      className="btn-add"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleProductAction(item);
                      }}
                    >
                      {composable ? 'Composer' : 'Ajouter'}
                    </button>
                  </div>
                </div>
              </article>
            );
            })}
          </div>
        ) : (
          <div className="menu-empty-state">
            <p>Aucun plat ne correspond à votre recherche.</p>
            <button type="button" className="btn btn-primary" onClick={clearSearch}>
              Voir tout le menu
            </button>
          </div>
        )}
      </section>

      {selectedProduct && isComposableProduct(selectedProduct) && (
        <MealComposerModal product={selectedProduct} onClose={closeComposer} />
      )}
    </div>
  );
}

export default Menu;
